"use client";

import { useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Card, CODE, GradText, SectionHead, SectionShell, Terminal } from "./kit";
import { gsap, insure, NOT_REDUCED, useEnterStagger, useGSAP } from "./kit/motion";
import { routePath } from "@/lib/routes";

type Format = "stats" | "stream" | "export";

const FORMATS: { id: Format; label: string }[] = [
  { id: "stats", label: "Stats" },
  { id: "stream", label: "Stream" },
  { id: "export", label: "Export" },
];

/** Retention + API rate per plan — values mirror components/pricing/data.ts
 *  (TIERS / USAGE_DIMENSIONS, from backend services/plans.py). Analytics
 *  itself isn't plan-gated: every plan gets the same dashboard, API and
 *  export; the request-log export is clamped to the plan's retention. */
const TIERS = [
  { name: "Free", retention: "30 days", exportNote: "Dashboard, API + CSV/JSON export · 60 req/min" },
  { name: "Premium", retention: "1 year", exportNote: "Dashboard, API + CSV/JSON export · 600 req/min", featured: true },
  { name: "Enterprise", retention: "Unlimited", exportNote: "Dashboard, API + CSV/JSON export · custom rate limits" },
];

const LINK_ID = "lnk_8h2f0qZ3Tk1mR4vXw9aBcQ";

const REQUEST: Record<Format, React.ReactNode> = {
  stats: (
    <>
      <span className={CODE.comment}>$</span> curl{" "}
      <span className={CODE.string}>&quot;https://api.rii.link/v1/links/{LINK_ID}/stats?group_by=country&quot;</span> \
      {"\n"}  -H <span className={CODE.string}>&quot;Authorization: Bearer $RII_KEY&quot;</span>
    </>
  ),
  stream: (
    <>
      <span className={CODE.comment}>$</span> curl -N https://api.rii.link/v1/events/stream \
      {"\n"}  -H <span className={CODE.string}>&quot;Authorization: Bearer $RII_KEY&quot;</span>
    </>
  ),
  export: (
    <>
      <span className={CODE.comment}>$</span> curl -D - -o clicks.csv{" "}
      <span className={CODE.string}>&quot;https://api.rii.link/v1/export/request-logs?format=csv&amp;from=2026-01-01&quot;</span> \
      {"\n"}  -H <span className={CODE.string}>&quot;Authorization: Bearer $RII_KEY&quot;</span>
    </>
  ),
};

const RESPONSE: Record<Format, React.ReactNode> = {
  stats: (
    <>
      {"{"}
      {"\n"}  <span className={CODE.key}>&quot;total&quot;</span>: <span className={CODE.number}>12408</span>,
      {"\n"}  <span className={CODE.key}>&quot;group_by&quot;</span>: <span className={CODE.string}>&quot;country&quot;</span>,
      {"\n"}  <span className={CODE.key}>&quot;buckets&quot;</span>: [
      {"\n"}    {"{ "}<span className={CODE.key}>&quot;key&quot;</span>: <span className={CODE.string}>&quot;DE&quot;</span>, <span className={CODE.key}>&quot;clicks&quot;</span>: <span className={CODE.number}>4715</span>{" },"}
      {"\n"}    {"{ "}<span className={CODE.key}>&quot;key&quot;</span>: <span className={CODE.string}>&quot;US&quot;</span>, <span className={CODE.key}>&quot;clicks&quot;</span>: <span className={CODE.number}>3350</span>{" },"}
      {"\n"}    {"{ "}<span className={CODE.key}>&quot;key&quot;</span>: <span className={CODE.string}>&quot;FR&quot;</span>, <span className={CODE.key}>&quot;clicks&quot;</span>: <span className={CODE.number}>1861</span>{" }"}
      {"\n"}  ],
      {"\n"}  <span className={CODE.key}>&quot;interval&quot;</span>: <span className={CODE.number}>null</span>,{" "}
      <span className={CODE.key}>&quot;series&quot;</span>: [],
      {"\n"}  <span className={CODE.key}>&quot;from&quot;</span>: <span className={CODE.string}>&quot;2026-07-13T00:00:00Z&quot;</span>,
      {"\n"}  <span className={CODE.key}>&quot;to&quot;</span>: <span className={CODE.string}>&quot;2026-08-12T00:00:00Z&quot;</span>
      {"\n"}
      {"}"}
    </>
  ),
  stream: (
    <>
      <span className={CODE.key}>event:</span> click
      {"\n"}<span className={CODE.key}>data:</span> {"{"}<span className={CODE.string}>&quot;link_id&quot;</span>:<span className={CODE.string}>&quot;{LINK_ID}&quot;</span>,
      <span className={CODE.string}>&quot;country&quot;</span>:<span className={CODE.string}>&quot;DE&quot;</span>,
      <span className={CODE.string}>&quot;device&quot;</span>:<span className={CODE.string}>&quot;mobile&quot;</span>,
      <span className={CODE.string}>&quot;referrer&quot;</span>:<span className={CODE.string}>&quot;instagram.com&quot;</span>,
      <span className={CODE.string}>&quot;utm_source&quot;</span>:<span className={CODE.string}>&quot;instagram&quot;</span>,
      <span className={CODE.string}>&quot;utm_medium&quot;</span>:<span className={CODE.number}>null</span>,
      <span className={CODE.string}>&quot;utm_campaign&quot;</span>:<span className={CODE.string}>&quot;spring&quot;</span>,
      <span className={CODE.string}>&quot;is_qr&quot;</span>:<span className={CODE.number}>false</span>,
      <span className={CODE.string}>&quot;rule_matched&quot;</span>:<span className={CODE.string}>&quot;geo.country == \&quot;DE\&quot;&quot;</span>,
      <span className={CODE.string}>&quot;timestamp&quot;</span>:<span className={CODE.string}>&quot;2026-08-12T09:17:02.418+00:00&quot;</span>{"}"}
      {"\n\n"}
      <span className={CODE.comment}>… one frame per human click on your API-created links</span>
    </>
  ),
  export: (
    <>
      HTTP/2 <span className={CODE.number}>200</span>
      {"\n"}<span className={CODE.key}>content-type:</span> text/csv; charset=utf-8
      {"\n"}<span className={CODE.key}>content-disposition:</span> attachment; filename=<span className={CODE.string}>&quot;rii-request-logs-acme-2026-09-28.csv&quot;</span>
      {"\n"}<span className={CODE.key}>x-export-from:</span> <span className={CODE.string}>2026-08-29T09:17:02+00:00</span>
      {"\n"}<span className={CODE.key}>x-export-to:</span> <span className={CODE.string}>2026-09-28T09:17:02+00:00</span>
      {"\n"}<span className={CODE.key}>x-export-requested-from:</span> <span className={CODE.string}>2026-01-01T00:00:00+00:00</span>
      {"\n"}<span className={CODE.key}>x-export-clamped:</span> <span className={CODE.number}>true</span>
      {"\n"}<span className={CODE.key}>x-export-retention-days:</span> <span className={CODE.number}>30</span>
      {"\n\n"}
      <span className={CODE.comment}>… one row per click, streamed; from= was clamped to the Free plan&apos;s 30 days</span>
    </>
  ),
};

/**
 * API access, export & retention — the real public API: aggregated stats
 * (GET /v1/links/{id}/stats), the live SSE click stream
 * (GET /v1/events/stream) and the request-log export
 * (GET /v1/export/request-logs, clamped to the plan's retention), all
 * limited to API-created links, next to the per-plan retention tiers
 * (values from the pricing data). The dashboard exports the whole
 * workspace (Settings → Export). Light beat; the dark terminal chrome
 * anchors it.
 */
export default function ExportSection() {
  const [format, setFormat] = useState<Format>("stats");

  const bodyRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<HTMLDivElement>(null);
  const prevFormat = useRef(format);

  useEnterStagger(bodyRef, "[data-exp-tier]", { y: 24, stagger: 0.1 });

  // Terminal "prints" — request types in, response wipes down (the
  // LlmShowcase pattern).
  const print = () => {
    const tl = gsap
      .timeline({ defaults: { overwrite: "auto" } })
      .fromTo(
        "[data-t-request]",
        { clipPath: "inset(-2px 100% -2px 0)" },
        { clipPath: "inset(-2px 0% -2px 0)", duration: 0.45, ease: "steps(18)" },
      )
      .fromTo(
        "[data-t-response]",
        { clipPath: "inset(-2px -2px 100% -2px)" },
        { clipPath: "inset(-2px -2px -2px -2px)", duration: 0.65, ease: "steps(12)" },
        ">-0.05",
      );
    insure(tl);
    return tl;
  };

  // First scroll-enter plays the print once; if it never fires the terminal
  // simply shows its content unanimated.
  useGSAP(
    (_, contextSafe) => {
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        const el = termRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
              io.disconnect();
              contextSafe!(print)();
            }
          },
          { rootMargin: "0px 0px -15% 0px" },
        );
        io.observe(el);
        return () => io.disconnect();
      });
    },
    { scope: termRef },
  );

  // Every format switch re-prints (skip the initial mount).
  useGSAP(
    (_, contextSafe) => {
      if (prevFormat.current === format) return;
      prevFormat.current = format;
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        contextSafe!(print)();
      });
    },
    { dependencies: [format], scope: termRef },
  );

  const toggle = (
    <span className="flex gap-1 rounded-lg bg-[#0d0d12] p-[3px]">
      {FORMATS.map((f) => (
        <button
          key={f.id}
          type="button"
          onClick={() => setFormat(f.id)}
          className={`rounded-md px-2.5 py-1 font-jetbrains text-xs transition ${
            format === f.id ? "bg-[#26262f] text-white" : "text-[#9b9bad]"
          }`}
        >
          {f.label}
        </button>
      ))}
    </span>
  );

  return (
    <SectionShell id="ref-export">
      <SectionHead
        center
        eyebrow="API, export & retention"
        title={
          <>
            Your clicks, <GradText>queryable &amp; exportable.</GradText>
          </>
        }
        lead="Clicks on your API-created links are queryable within seconds — JSON stats over any from/to window, or a live click stream. Download the raw click log as CSV or JSON from the dashboard, or over the API; exports reach back as far as your plan's retention."
      />

      <div
        ref={bodyRef}
        data-grid-block
        className="grid items-center gap-12 lg:grid-cols-[1.15fr_1fr]"
      >
        {/* min-w-0 on both columns: the long request URL and SSE frame
            would otherwise stretch the mobile column to their min-content. */}
        <div ref={termRef} className="min-w-0">
          <Terminal path="~/rii — api" toggle={toggle} minHeight={300}>
            <pre className="m-0 whitespace-pre-wrap font-[inherit] [overflow-wrap:anywhere]">
              <div data-t-request>{REQUEST[format]}</div>
              {"\n"}
              <div data-t-response>{RESPONSE[format]}</div>
            </pre>
          </Terminal>
        </div>

        <div className="flex min-w-0 flex-col gap-3.5">
          {TIERS.map((t) => (
            <div key={t.name} data-exp-tier>
              <Card
                shadow={t.featured}
                className={`px-5 py-4 ${t.featured ? "border-brand-600/35" : ""}`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div className="text-[15.5px] font-bold">{t.name}</div>
                  <div className="font-jetbrains text-[13.5px] font-semibold text-brand-600">
                    {t.retention}
                  </div>
                </div>
                <div className="mt-1 text-[13.5px] text-muted">{t.exportNote}</div>
              </Card>
            </div>
          ))}
          <a
            href={routePath("pricing")}
            className="group mt-1 inline-flex items-center gap-1.5 text-[15px] font-semibold text-brand-600"
          >
            Compare plans
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>
      </div>
    </SectionShell>
  );
}
